/**
 * /devhub/files — Files & Preview (operator, Area D4). Surface Layer (/01). REQ-001.91 DevHub UI rebuild · Phase 4.
 *
 * Operator file surface: lists project files and previews them by type. Wired to the LIVE projects
 * read (`useProjects` → GET /api/projects) so the file scope reflects real workspace state — the
 * browser never calls a model, service-role, or raw SQL (paid_model_called=false).
 *
 * HONEST STATES (no fabrication):
 *  - File list: `project_files` is now readable (authenticated SELECT) but currently has 0 rows in prod
 *    and no DevHub row-read seam is mapped yet, so the list renders an honest empty state — never a faked row.
 *  - Preview generation (thumbnails): the `file_preview_generation` workflow is built but awaits worker
 *    activation, so that capability renders an honest BLOCKED-by-worker state.
 *  - Preview itself: reuses the DevHub-Kit `FilePreview`, which is §7.4 no-leakage — it NEVER renders
 *    `project_files.storage_uri` / internal paths / hashes (binding invariant UNSAFE_REF). Opaque refs only.
 *
 * Operator-only: routePolicy plane guard + DevHubPanelPage self-guard (useEntitlements).
 */
import { createFileRoute } from '@tanstack/react-router';
import { Column, Grid, InlineNotification, Tag, Tile } from '@carbon/react';

import { DevHubPanelPage } from '../components/devhub/DevHubPanelPage';
import { FilePreview } from '../components/devhub-kit/FilePreview';
import {
  HonestStateBlocked,
  HonestStateEmpty,
  HonestStateLoading,
} from '../components/honest-state';
import { useProjects } from '../lib/persistence/useRuntimeData';

/** Preview kinds the operator FilePreview can render — shown as a capability legend (no live file bound). */
const PREVIEW_KINDS: ReadonlyArray<{ kind: string; label: string }> = [
  { kind: 'image', label: 'Image (signed read URL)' },
  { kind: 'pdf', label: 'PDF (metadata only)' },
  { kind: 'text', label: 'Text' },
  { kind: 'json', label: 'JSON' },
  { kind: 'metadata_only', label: 'Metadata only' },
  { kind: 'blocked', label: 'Blocked (governance)' },
];

function FilesPage() {
  const { projects, tenantContextMissing, isLoading } = useProjects();

  return (
    <DevHubPanelPage
      title="Files & Preview"
      subtitle="Project files and preview-by-type. Files are read through a governed projection — storage paths and hashes are never exposed."
      status={{ label: 'partial — files pending', live: false }}
    >
      <div className="surface-grid">
        <Grid condensed>
          {/* Scope / safety banner — what this surface reads and what it never exposes. */}
          <Column sm={4} md={8} lg={16}>
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title="Read-only file surface (safe projection)"
              subtitle="The file list uses the safe project_files projection only — storage paths, signed URIs and hashes never enter this surface (opaque refs only). Reading is authenticated and tenant-resolved server-side; the browser never holds a database client, calls a model, or issues raw SQL. Uploading, deleting, or re-classifying a file routes through a governed ActionFrame candidate, never a direct browser write."
            />
          </Column>

          {/* LIVE read: the project scope these files belong to (GET /api/projects). */}
          <Column sm={4} md={8} lg={16}>
            <div className="view-tile__row">
              <h2 className="view-tile__value">
                Project scope{!isLoading && !tenantContextMissing ? ` (${projects.length})` : ''}
              </h2>
              <div className="notification-row__meta">
                <Tag type="blue" size="sm">
                  Live read · /api/projects
                </Tag>
                <Tag type="cool-gray" size="sm">
                  project_files · 0 rows
                </Tag>
              </div>
            </div>
          </Column>

          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              {isLoading ? (
                <HonestStateLoading
                  variant="text"
                  rowCount={3}
                  ariaLabel="Loading project scope"
                />
              ) : tenantContextMissing ? (
                <HonestStateEmpty
                  title="No workspace context"
                  subtitle="A workspace must be provisioned and selected before its project files can be listed. Provision or switch to a workspace, then return to this surface."
                />
              ) : projects.length === 0 ? (
                <HonestStateEmpty
                  title="No projects in scope"
                  subtitle="This workspace has no projects yet, so there are no project files to list. Files appear here once a project exists and files are ingested into it."
                />
              ) : (
                <p className="view-tile__label">
                  {projects.length} project{projects.length === 1 ? '' : 's'} in scope. Files are
                  ingested per project and listed below once the file projection is populated.
                </p>
              )}
            </Tile>
          </Column>

          {/* HONEST: the file list itself — project_files is readable but has 0 rows; no fabricated rows. */}
          <Column sm={4} md={8} lg={16}>
            <h2 className="view-tile__value">Files</h2>
          </Column>

          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <HonestStateEmpty
                title="No files to display"
                subtitle="project_files is readable (authenticated SELECT) but currently has 0 rows in this environment, and no DevHub file-row read route is mapped yet. Files appear here once they are ingested through the governed pipeline — none are fabricated. Each row will expose an opaque file reference only, never its storage path or hash."
              />
            </Tile>
          </Column>

          {/* REUSE: FilePreview capability — preview-by-type with no live file bound (no leakage). */}
          <Column sm={4} md={8} lg={16}>
            <div className="view-tile__row">
              <h2 className="view-tile__value">Preview</h2>
              <div className="notification-row__meta">
                {PREVIEW_KINDS.map((k) => (
                  <Tag key={k.kind} type="gray" size="sm">
                    {k.label}
                  </Tag>
                ))}
              </div>
            </div>
          </Column>

          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <FilePreview
                fileName="No file selected"
                contentType="—"
                previewKind="metadata_only"
              />
            </Tile>
          </Column>

          {/* HONEST: thumbnail / preview GENERATION is a worker-gated workflow awaiting activation. */}
          <Column sm={4} md={8} lg={16}>
            <h2 className="view-tile__value">Preview generation</h2>
          </Column>

          <Column sm={4} md={8} lg={16}>
            <HonestStateBlocked
              title="Preview generation pending worker"
              ceremony="file_preview_generation worker activation"
              tone="info"
            />
          </Column>

          <Column sm={4} md={8} lg={16}>
            <p className="view-tile__label">
              The thumbnail / preview-rendering workflow (file_preview_generation) is built but awaits
              worker activation, so generated previews are not produced from the browser. Until the
              worker is activated, supported types render inline via the safe projection above and
              unsupported types degrade to a metadata-only panel. Generation, like every file
              mutation, runs server-side through the governed pipeline — never as a browser write or a
              model call.
            </p>
          </Column>
        </Grid>
      </div>
    </DevHubPanelPage>
  );
}

export const Route = createFileRoute('/devhub/files')({ component: FilesPage });
